import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Card, Row, Col, Typography, Flex, Progress, Radio, Button, Table, Tag, Avatar, Badge, Spin, Empty } from "antd";
import { PhoneOutlined, CheckCircleOutlined, ClockCircleOutlined, ContactsOutlined, CalendarOutlined, RightOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import Chart from "chart.js/auto";
import { getEmployeeLeads } from "../../services/leadService";
import { getEmployeeCallStats, getEmployeeAnnualTargets } from "../../services/employeeService";
import { getInvoices } from "../../services/invoiceService";
import { getTodayActivities } from "../../services/activityService";
import { getStagnantDeals } from "../../services/pipelineService";
import "./userOverview.css";

const { Title, Text, Paragraph } = Typography;

const LOCAL_STORAGE_STUCK_DEALS_COL_WIDTHS_KEY = "dealvoice_user_stuck_deals_col_widths";

const DEFAULT_STUCK_DEALS_COL_WIDTHS = {
  company: 190,
  contact: 150,
  stage: 140,
  inactivity: 110,
  amount: 90,
  lastActivity: 95,
};

function getSavedStuckDealsColWidths() {
  try {
    const saved = localStorage.getItem(LOCAL_STORAGE_STUCK_DEALS_COL_WIDTHS_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return { ...DEFAULT_STUCK_DEALS_COL_WIDTHS, ...parsed };
    }
  } catch (e) {
    // Ignore localStorage parse errors
  }
  return DEFAULT_STUCK_DEALS_COL_WIDTHS;
}

const ResizableHeaderCell = (props) => {
  const { onResize, width, children, ...restProps } = props;

  if (!width) {
    return <th {...restProps}>{children}</th>;
  }

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.pageX;
    const startWidth = width;

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.pageX - startX;
      const nextWidth = Math.max(60, startWidth + deltaX);
      if (onResize) {
        onResize(nextWidth);
      }
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <th {...restProps} style={{ ...restProps.style, width, position: "relative" }}>
      {children}
      <button type="button" aria-label="Resize column" className="table-resizable-handle" onMouseDown={handleMouseDown} />
    </th>
  );
};

const STAGE_TAG_COLORS = {
  QUALIFICATION: "blue",
  NEEDS_ANALYSIS: "cyan",
  VALUE_PROPOSITION: "purple",
  PROPOSAL_QUOTE: "geekblue",
  NEGOTIATION_REVIEW: "orange",
  CLOSED_WON: "success",
  CLOSED_LOST: "error",
  Pipeline: "blue",
  "Follow Up": "processing",
  Connected: "success",
  "Not Connected": "warning",
  NEW: "default",
};

const PALETTE = {
  outgoing: "#10b981",
  incoming: "#3b82f6",
  missed: "#ef4444",
  rejected: "#f59e0b",
};

const getPeriodLabel = (p) => {
  if (p === "today") return "Today";
  if (p === "yesterday") return "Yesterday";
  return "Last 7 Days";
};

const renderConnectRateFormat = () => <CheckCircleOutlined style={{ color: "#10b981", fontSize: 16 }} />;

// Formats duration in seconds to "Xh Ym" or "Xm Ys"
function formatDuration(seconds = 0) {
  const s = Math.round(Number(seconds) || 0);
  if (s <= 0) return "0s";
  const hrs = Math.floor(s / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const remS = s % 60;
  if (hrs > 0) return `${hrs}h ${mins}m`;
  if (mins > 0) return `${mins}m ${remS}s`;
  return `${s}s`;
}

const LAST_WEEK_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TODAY_LABELS = ["09 AM", "11 AM", "01 PM", "03 PM", "05 PM", "07 PM"];

const LAST_WEEK_TOTAL_WEIGHTS = [0.14, 0.22, 0.18, 0.24, 0.14, 0.05, 0.03];
const TODAY_TOTAL_WEIGHTS = [0.12, 0.26, 0.18, 0.28, 0.11, 0.05];

const LAST_WEEK_CONNECTED_WEIGHTS = [0.14, 0.24, 0.18, 0.22, 0.14, 0.05, 0.03];
const TODAY_CONNECTED_WEIGHTS = [0.1, 0.28, 0.18, 0.3, 0.09, 0.05];

const applyWeights = (value, weights) => weights.map((w) => Math.round(value * w));

function getTimelineTrendData(period, totalCalls, connectedCalls) {
  if (period === "lastweek") {
    return {
      labels: LAST_WEEK_LABELS,
      totalData: applyWeights(totalCalls, LAST_WEEK_TOTAL_WEIGHTS),
      connectedData: applyWeights(connectedCalls, LAST_WEEK_CONNECTED_WEIGHTS),
    };
  }
  return {
    labels: TODAY_LABELS,
    totalData: applyWeights(totalCalls, TODAY_TOTAL_WEIGHTS),
    connectedData: applyWeights(connectedCalls, TODAY_CONNECTED_WEIGHTS),
  };
}

function buildCallTrendDatasets({ isBar, totalData, connectedData, grad }) {
  if (isBar) {
    return [
      {
        label: "Total Calls",
        data: totalData,
        borderColor: "#2459d3",
        backgroundColor: "#2459d3",
        fill: false,
        tension: 0,
        pointRadius: 0,
        pointHoverRadius: 7,
        pointBackgroundColor: "#2459d3",
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2,
        borderWidth: 0,
        borderRadius: 6,
        barPercentage: 0.45,
      },
      {
        label: "Connected",
        data: connectedData,
        borderColor: "#10b981",
        backgroundColor: "#10b981",
        fill: false,
        tension: 0,
        pointRadius: 0,
        pointHoverRadius: 7,
        pointBackgroundColor: "#10b981",
        pointBorderColor: "#ffffff",
        pointBorderWidth: 2,
        borderWidth: 0,
        borderRadius: 6,
        barPercentage: 0.45,
      },
    ];
  }

  return [
    {
      label: "Total Calls",
      data: totalData,
      borderColor: "#2459d3",
      backgroundColor: grad,
      fill: true,
      tension: 0.45,
      pointRadius: 4,
      pointHoverRadius: 7,
      pointBackgroundColor: "#2459d3",
      pointBorderColor: "#ffffff",
      pointBorderWidth: 2,
      borderWidth: 3.5,
      borderRadius: 0,
    },
    {
      label: "Connected",
      data: connectedData,
      borderColor: "#10b981",
      backgroundColor: "transparent",
      fill: false,
      tension: 0.45,
      pointRadius: 4,
      pointHoverRadius: 7,
      pointBackgroundColor: "#10b981",
      pointBorderColor: "#ffffff",
      pointBorderWidth: 2,
      borderWidth: 2.5,
      borderRadius: 0,
    },
  ];
}

const CALL_TREND_CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: {
      display: true,
      position: "top",
      align: "end",
      labels: {
        boxWidth: 8,
        boxHeight: 8,
        usePointStyle: true,
        pointStyle: "circle",
        font: { size: 12, weight: "500" },
        color: "#64748b",
        padding: 16,
      },
    },
    tooltip: {
      backgroundColor: "#0f172a",
      titleColor: "#ffffff",
      bodyColor: "#94a3b8",
      padding: 10,
      cornerRadius: 8,
      callbacks: {
        label: (item) => ` ${item.dataset.label}: ${item.raw} calls`,
      },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: "#94a3b8", font: { size: 11, weight: "500" }, padding: 6 },
      border: { display: false },
    },
    y: {
      display: true,
      beginAtZero: true,
      grid: { color: "#f1f5f9" },
      ticks: { color: "#94a3b8", font: { size: 10 }, stepSize: 2, padding: 6 },
      border: { display: false },
    },
  },
};

/**
 * ── Interactive Timeline / Call Trend Chart Component ──
 */
function CallTrendTimelineChart({ chartView = "timeline", period = "today", totalCalls = 0, connectedCalls = 0 }) {
  const canvasRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = canvasRef.current.getContext("2d");
    const grad = ctx.createLinearGradient(0, 0, 0, 200);
    grad.addColorStop(0, "rgba(36, 89, 211, 0.25)");
    grad.addColorStop(1, "rgba(36, 89, 211, 0.0)");

    const isBar = chartView === "breakdown";
    const { labels, totalData, connectedData } = getTimelineTrendData(period, totalCalls, connectedCalls);
    const datasets = buildCallTrendDatasets({ isBar, totalData, connectedData, grad });

    chartInstance.current = new Chart(canvasRef.current, {
      type: isBar ? "bar" : "line",
      data: {
        labels,
        datasets,
      },
      options: CALL_TREND_CHART_OPTIONS,
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [chartView, period, totalCalls, connectedCalls]);

  return (
    <div style={{ height: 210, width: "100%", position: "relative" }}>
      <canvas ref={canvasRef} />
    </div>
  );
}

/**
 * ── Interactive Call Mix Pie / Donut Chart Component ──
 */
function CallMixPieChart({ stats }) {
  const canvasRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!canvasRef.current) return;

    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const counts = [stats?.outgoing || 0, stats?.incoming || 0, stats?.missed || 0, stats?.rejected || 0];

    const hasData = counts.some((c) => c > 0);

    chartInstance.current = new Chart(canvasRef.current, {
      type: "doughnut",
      data: {
        labels: ["Outgoing", "Incoming", "Missed", "Rejected"],
        datasets: [
          {
            data: hasData ? counts : [1, 0, 0, 0],
            backgroundColor: hasData ? [PALETTE.outgoing, PALETTE.incoming, PALETTE.missed, PALETTE.rejected] : ["#e2e8f0", "#e2e8f0", "#e2e8f0", "#e2e8f0"],
            borderWidth: 3,
            borderColor: "#ffffff",
            hoverOffset: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "70%",
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#0f172a",
            titleColor: "#ffffff",
            bodyColor: "#94a3b8",
            padding: 10,
            cornerRadius: 8,
          },
        },
      },
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [stats]);

  return (
    <div style={{ height: "100%", width: "100%", position: "relative" }}>
      <canvas ref={canvasRef} />
    </div>
  );
}

const isDateToday = (dateVal) => {
  if (!dateVal) return false;
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return false;
  const today = new Date();
  return d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate();
};

function processTodayActivities(leadsRes, activitiesRes) {
  const allLeads = leadsRes?.items || leadsRes?.leads || [];
  const sortedLeads = [...allLeads].sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
  const recentLeads = sortedLeads.slice(0, 5);

  const directActivities = Array.isArray(activitiesRes?.data) ? activitiesRes.data : [];
  const todayLeadFollowups = allLeads.filter((l) => isDateToday(l.followUpDate) || isDateToday(l.reminderDate));

  const items = [];
  const seenLeadIds = new Set();

  directActivities.forEach((act) => {
    if (act.leadId) seenLeadIds.add(String(act.leadId));
    const actDate = act.activityDate ? new Date(act.activityDate) : null;
    const timeStr = actDate && !isNaN(actDate.getTime()) ? actDate.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : "Today";
    const actType = act.type ? act.type.charAt(0).toUpperCase() + act.type.slice(1) : "Task";

    items.push({
      id: act._id || act.id || `act-${Math.random()}`,
      title: act.leadCompanyName || act.companyName || act.title || "Scheduled Activity",
      subtitle: act.contactName || act.description || act.title || "Today's Task",
      tag: timeStr !== "Today" ? timeStr : actType,
      tagColor: act.type === "call" ? "green" : act.type === "meeting" ? "purple" : "blue",
    });
  });

  todayLeadFollowups.forEach((lead) => {
    const leadId = lead._id || lead.id || lead.leadId;
    if (!leadId || !seenLeadIds.has(String(leadId))) {
      const fDate = lead.followUpDate || lead.reminderDate ? new Date(lead.followUpDate || lead.reminderDate) : null;
      const timeStr = fDate && !isNaN(fDate.getTime()) && (fDate.getHours() !== 0 || fDate.getMinutes() !== 0) ? fDate.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : "Today";

      items.push({
        id: leadId || `lead-${Math.random()}`,
        title: lead.leadCompanyName || lead.companyName || "Follow-up Lead",
        subtitle: lead.contactName || lead.contactNumber || "Primary Contact",
        tag: timeStr,
        tagColor: "blue",
      });
    }
  });

  return { allLeads, recentLeads, todayActivities: items.slice(0, 6) };
}

function getInvoicePaidAmount(inv) {
  return Number(inv.amountPaid) || (inv.paymentStatus === "paid" ? Number(inv.total || 0) : 0);
}

function calculateInvoiceAchieved(invoices, currentYear, currentMonth) {
  if (!Array.isArray(invoices) || invoices.length === 0) return 0;

  const currentMonthAchieved = invoices.reduce((sum, inv) => {
    const invD = inv.invoiceDate ? new Date(inv.invoiceDate) : new Date(inv.createdAt);
    if (invD.getFullYear() === currentYear && invD.getMonth() + 1 === currentMonth) {
      return sum + getInvoicePaidAmount(inv);
    }
    return sum;
  }, 0);

  if (currentMonthAchieved > 0) {
    return currentMonthAchieved;
  }

  // Fallback across period if month has just rolled over
  return invoices.reduce((sum, inv) => sum + getInvoicePaidAmount(inv), 0);
}

function resolveTargetAndAchieved(targetRes, invoiceRes, currentYear, currentMonth) {
  let assignedTarget = 100000;
  let liveAchieved = 0;

  if (targetRes?.success && Array.isArray(targetRes.data)) {
    const monthTarget = targetRes.data.find((d) => Number(d.month) === currentMonth);
    if (monthTarget) {
      if (Number(monthTarget.targetAmount) > 0) {
        assignedTarget = Number(monthTarget.targetAmount);
      }
      liveAchieved = Number(monthTarget.achievedAmount) || 0;
    }
  }

  if (liveAchieved === 0 && invoiceRes?.success) {
    const empInvoices = invoiceRes.invoices || invoiceRes.items;
    liveAchieved = calculateInvoiceAchieved(empInvoices, currentYear, currentMonth);
  }

  return {
    targetAmount: assignedTarget,
    achievedAmount: liveAchieved,
  };
}

function UserOverview({ onNavigateTab, onReady }) {
  const onReadyRef = useRef(onReady);
  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  const initialLoadDone = useRef(false);

  const [period, setPeriod] = useState("today");
  const [chartView, setChartView] = useState("timeline"); // 'timeline' | 'breakdown'
  const [loading, setLoading] = useState(false);
  const [callStats, setCallStats] = useState({
    total: 0,
    connected: 0,
    totalDuration: 0,
    incoming: 0,
    outgoing: 0,
    missed: 0,
    rejected: 0,
  });
  const [leads, setLeads] = useState([]);
  const [recentLeads, setRecentLeads] = useState([]);
  const [stuckDeals, setStuckDeals] = useState([]);
  const [upcomingFollowups, setUpcomingFollowups] = useState([]);
  const [targetData, setTargetData] = useState({
    targetAmount: 0,
    achievedAmount: 0,
  });

  const user = useSelector((state) => state.session?.settings?.userInfo);
  const companyInfo = useSelector((state) => state.executor?.companyInfo);
  const companyName = companyInfo?.companyName || user?.companyName || "";
  const companyCode = companyInfo?.companyCode || user?.companyCode || "";
  const employeeId = user?.id || user?._id || user?.employeeId;
  const employeeName = user?.name || "Sales Representative";
  const employeeFirstName = employeeName.split(" ")[0];

  const getGreeting = () => {
    const hr = new Date().getHours();
    if (hr < 12) return "Good morning";
    if (hr < 17) return "Good afternoon";
    return "Good evening";
  };

  const fetchData = useCallback(async () => {
    if (!companyCode || !employeeId) return;
    setLoading(true);
    try {
      const statsRes = await getEmployeeCallStats(companyCode, employeeId, period);
      if (statsRes?.stats) {
        setCallStats(statsRes.stats);
      }

      const [leadsRes, activitiesRes, stagnantRes] = await Promise.all([getEmployeeLeads(companyCode, employeeId), getTodayActivities(companyCode, { employeeId }), getStagnantDeals(companyCode, { days: 7, employeeId })]);
      const { allLeads, recentLeads, todayActivities } = processTodayActivities(leadsRes, activitiesRes);
      setLeads(allLeads);
      setRecentLeads(recentLeads);
      setUpcomingFollowups(todayActivities);

      let fetchedStuckDeals = Array.isArray(stagnantRes?.deals) ? stagnantRes.deals : [];
      if (fetchedStuckDeals.length === 0 && Array.isArray(allLeads)) {
        const now = Date.now();
        const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
        fetchedStuckDeals = allLeads
          .filter((lead) => {
            const date = new Date(lead.updatedAt || lead.createdAt || 0).getTime();
            const isOld = now - date >= sevenDaysMs;
            const status = String(lead.status || "").toLowerCase();
            const isOpen = status !== "converted" && status !== "lost" && status !== "closed";
            return isOld && isOpen;
          })
          .map((lead) => {
            const date = new Date(lead.updatedAt || lead.createdAt || Date.now()).getTime();
            const daysInStage = Math.floor((now - date) / (1000 * 60 * 60 * 24));
            return {
              _id: lead._id || lead.id,
              leadCompanyName: lead.companyName || lead.clientCompanyName || lead.leadCompanyName || "Unknown Company",
              contactName: lead.contactName || lead.primaryContact || lead.name || "",
              contactNumber: lead.contactNumber || lead.phone || lead.mobile || "",
              amount: lead.amount || lead.dealValue || 0,
              pipelineStage: lead.status || "QUALIFICATION",
              stageLabel: lead.status || "Pipeline",
              daysInStage,
              stageChangedAt: lead.updatedAt || lead.createdAt,
            };
          });
      }
      setStuckDeals(fetchedStuckDeals);

      // Fetch live dynamic target & achieved revenue from middleware
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;
      const [targetRes, invoiceRes] = await Promise.all([getEmployeeAnnualTargets(companyCode, employeeId, currentYear), getInvoices(companyCode, { employeeId })]);

      setTargetData(resolveTargetAndAchieved(targetRes, invoiceRes, currentYear, currentMonth));
    } catch (err) {
      console.error("[UserOverview] fetch error:", err);
    } finally {
      setLoading(false);
      if (!initialLoadDone.current) {
        initialLoadDone.current = true;
        if (onReadyRef.current) {
          onReadyRef.current();
        }
      }
    }
  }, [companyCode, employeeId, period]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalCalls = callStats.total || callStats.incoming + callStats.outgoing + callStats.missed + callStats.rejected || 0;
  const connectRate = totalCalls > 0 ? Math.round(((callStats.connected || 0) / totalCalls) * 100) : 0;
  const targetPercent = targetData.targetAmount > 0 ? Math.min(100, Math.round((targetData.achievedAmount / targetData.targetAmount) * 100)) : 0;

  const [stuckDealsColWidths, setStuckDealsColWidths] = useState(getSavedStuckDealsColWidths);

  const handleResizeStuckDealsCol = useCallback(
    (key) => (width) => {
      setStuckDealsColWidths((prev) => {
        const next = { ...prev, [key]: width };
        try {
          localStorage.setItem(LOCAL_STORAGE_STUCK_DEALS_COL_WIDTHS_KEY, JSON.stringify(next));
        } catch (e) {
          // Ignore localStorage errors
        }
        return next;
      });
    },
    [],
  );

  const totalStuckDealsTableWidth = useMemo(() => Object.values(stuckDealsColWidths).reduce((sum, w) => sum + (Number(w) || 90), 0), [stuckDealsColWidths]);

  // Stuck Deals table columns (more than 7 days) with resizable single-line functionality
  const stuckDealsColumns = useMemo(
    () => [
      {
        title: "Company",
        dataIndex: "leadCompanyName",
        key: "company",
        width: stuckDealsColWidths.company,
        ellipsis: true,
        onHeaderCell: () => ({
          width: stuckDealsColWidths.company,
          onResize: handleResizeStuckDealsCol("company"),
        }),
        render: (text, record) => {
          const compName = text || record.dealName || "—";
          return (
            <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={compName}>
              <Text strong style={{ color: "#0f172a", fontSize: "0.82rem" }}>
                {compName}
              </Text>
            </div>
          );
        },
      },
      {
        title: "Contact",
        key: "contact",
        width: stuckDealsColWidths.contact,
        ellipsis: true,
        onHeaderCell: () => ({
          width: stuckDealsColWidths.contact,
          onResize: handleResizeStuckDealsCol("contact"),
        }),
        render: (_, record) => {
          const contactStr = [record.contactName, record.contactNumber].filter(Boolean).join(" • ") || "—";
          return (
            <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={contactStr}>
              <Text style={{ fontSize: "0.8rem", color: "#334155" }}>{contactStr}</Text>
            </div>
          );
        },
      },
      {
        title: "Status / Stage",
        dataIndex: "pipelineStage",
        key: "stage",
        width: stuckDealsColWidths.stage,
        ellipsis: true,
        onHeaderCell: () => ({
          width: stuckDealsColWidths.stage,
          onResize: handleResizeStuckDealsCol("stage"),
        }),
        render: (stage, record) => {
          const label = record.stageLabel || stage || "Pipeline";
          const color = STAGE_TAG_COLORS[stage] === "orange" ? "#d97706" : STAGE_TAG_COLORS[stage] === "blue" ? "#2563eb" : STAGE_TAG_COLORS[stage] === "cyan" ? "#0891b2" : STAGE_TAG_COLORS[stage] === "purple" ? "#7c3aed" : "#334155";
          return (
            <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={label}>
              <Text style={{ color, fontWeight: 500, fontSize: "0.8rem" }}>{label}</Text>
            </div>
          );
        },
      },
      {
        title: "Inactivity",
        dataIndex: "daysInStage",
        key: "inactivity",
        width: stuckDealsColWidths.inactivity,
        onHeaderCell: () => ({
          width: stuckDealsColWidths.inactivity,
          onResize: handleResizeStuckDealsCol("inactivity"),
        }),
        render: (days) => {
          const d = Number.isFinite(days) ? days : 7;
          return (
            <div style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
              <Text style={{ color: "#ef4444", fontWeight: 600, fontSize: "0.8rem" }}>
                {d} {d === 1 ? "day" : "days"} stuck
              </Text>
            </div>
          );
        },
      },
      {
        title: "Amount",
        dataIndex: "amount",
        key: "amount",
        width: stuckDealsColWidths.amount,
        onHeaderCell: () => ({
          width: stuckDealsColWidths.amount,
          onResize: handleResizeStuckDealsCol("amount"),
        }),
        render: (amount) => (
          <div style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            <Text strong style={{ fontSize: "0.82rem", color: amount ? "#0f172a" : "#94a3b8", fontVariantNumeric: "tabular-nums" }}>
              {amount ? `₹${Number(amount).toLocaleString()}` : "—"}
            </Text>
          </div>
        ),
      },
      {
        title: "Last Activity",
        dataIndex: "stageChangedAt",
        key: "lastActivity",
        width: stuckDealsColWidths.lastActivity,
        align: "right",
        onHeaderCell: () => ({
          width: stuckDealsColWidths.lastActivity,
          onResize: handleResizeStuckDealsCol("lastActivity"),
        }),
        render: (date, record) => {
          const d = new Date(date || record.updatedAt || record.createdAt || Date.now());
          return (
            <div style={{ whiteSpace: "nowrap", overflow: "hidden" }}>
              <Text type="secondary" style={{ fontSize: "0.78rem" }}>
                {d.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </Text>
            </div>
          );
        },
      },
    ],
    [stuckDealsColWidths, handleResizeStuckDealsCol],
  );

  return (
    <Spin spinning={loading} description="Loading overview...">
      <Flex vertical gap={24} className="user-overview-container">
        {/* ── 1. GREETING BANNER CARD (Apple-Style Minimal Aesthetic) ── */}
        <Card bordered={false} className="user-greeting-hero-card">
          <div className="user-greeting-tech-overlay" aria-hidden="true">
            <svg viewBox="0 0 500 240" fill="none" xmlns="http://www.w3.org/2000/svg" className="user-greeting-tech-svg" preserveAspectRatio="xMaxYMid meet">
              <defs>
                <linearGradient id="kolamGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#ffffff" stopOpacity="0.15" />
                  <stop offset="50%" stopColor="#ffffff" stopOpacity="0.75" />
                  <stop offset="100%" stopColor="#ffffff" stopOpacity="0.95" />
                </linearGradient>
                <pattern id="dotGrid" x="0" y="0" width="22" height="22" patternUnits="userSpaceOnUse">
                  <circle cx="11" cy="11" r="1.1" fill="#ffffff" fillOpacity="0.45" />
                </pattern>
              </defs>

              {/* Fading Matrix Dot Grid */}
              <rect width="500" height="240" fill="url(#dotGrid)" opacity="0.6" />

              {/* Modern Geometric Kolam & Flowing Tech Contours */}
              <g stroke="url(#kolamGrad)" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" opacity="0.85">
                {/* Flowing Organic Contour Lines */}
                <path d="M 120,40 C 220,10 320,80 480,20" />
                <path d="M 80,90 C 200,50 300,140 490,70" />
                <path d="M 60,150 C 180,110 280,200 480,140" />
                <path d="M 100,210 C 220,170 340,240 500,190" />

                {/* Symmetrical Geometric Kolam Loops & Rhombus Matrix */}
                <path d="M 380,120 C 380,70 330,40 280,40 C 230,40 180,70 180,120 C 180,170 230,200 280,200 C 330,200 380,170 380,120 Z" />
                <path d="M 430,120 C 430,50 360,10 280,10 C 200,10 130,50 130,120 C 130,190 200,230 280,230 C 360,230 430,190 430,120 Z" />

                <path d="M 280,40 L 360,120 L 280,200 L 200,120 Z" />
                <path d="M 280,10 L 390,120 L 280,230 L 170,120 Z" />

                <path d="M 200,120 Q 280,60 360,120 Q 280,180 200,120 Z" />
                <path d="M 280,40 Q 340,120 280,200 Q 220,120 280,40 Z" />

                {/* Geometric Nodes */}
                <circle cx="280" cy="120" r="3" fill="#ffffff" fillOpacity="0.85" />
                <circle cx="280" cy="40" r="2.5" fill="#ffffff" fillOpacity="0.75" />
                <circle cx="360" cy="120" r="2.5" fill="#ffffff" fillOpacity="0.75" />
                <circle cx="280" cy="200" r="2.5" fill="#ffffff" fillOpacity="0.75" />
                <circle cx="200" cy="120" r="2.5" fill="#ffffff" fillOpacity="0.75" />
                <circle cx="337" cy="63" r="2" fill="#ffffff" fillOpacity="0.65" />
                <circle cx="337" cy="177" r="2" fill="#ffffff" fillOpacity="0.65" />
                <circle cx="223" cy="177" r="2" fill="#ffffff" fillOpacity="0.65" />
                <circle cx="223" cy="63" r="2" fill="#ffffff" fillOpacity="0.65" />
              </g>
            </svg>
          </div>

          <Flex vertical gap={4} className="user-greeting-content">
            <Title level={4} style={{ margin: 0, fontWeight: 700, color: "#ffffff", fontSize: "1.2rem", letterSpacing: "-0.015em" }}>
              {getGreeting()}, {employeeFirstName}
            </Title>
            <Paragraph style={{ margin: 0, color: "#94a3b8", fontSize: "0.82rem" }}>
              Track your active calls, revenue milestones, and client follow-ups for <strong>{companyName}</strong>.
            </Paragraph>
          </Flex>
        </Card>

        {/* ── 2. CONTROLS / FILTER BAR ── */}
        <Flex align="center" justify="space-between" wrap="wrap" gap={16}>
          <Flex align="center" gap={12}>
            <Radio.Group value={period} onChange={(e) => setPeriod(e.target.value)} buttonStyle="solid" size="middle">
              <Radio.Button value="today">Today</Radio.Button>
              <Radio.Button value="yesterday">Yesterday</Radio.Button>
              <Radio.Button value="lastweek">Last 7 Days</Radio.Button>
            </Radio.Group>
          </Flex>

          <Flex align="center" gap={12}>
            <Text type="secondary" style={{ fontSize: "0.8rem" }}>
              Active Period: <strong style={{ color: "#0f172a" }}>{getPeriodLabel(period)}</strong>
            </Text>
          </Flex>
        </Flex>

        {/* ── 3. TOP KPI METRIC CARDS ── */}
        <Row gutter={[16, 16]}>
          {/* Total Calls */}
          <Col xs={24} sm={12} lg={4} xl={4}>
            <Card bordered={false} className="user-kpi-card">
              <Flex justify="space-between" align="center">
                <Flex vertical gap={2}>
                  <div style={{ fontSize: "1.3rem", fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>{totalCalls}</div>
                  <Text type="secondary" style={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b" }}>
                    Total Calls
                  </Text>
                </Flex>
                <PhoneOutlined style={{ fontSize: "1rem", color: "#000000ff" }} />
              </Flex>
            </Card>
          </Col>

          {/* Connected */}
          <Col xs={24} sm={12} lg={4} xl={4}>
            <Card bordered={false} className="user-kpi-card">
              <Flex justify="space-between" align="center">
                <Flex vertical gap={2}>
                  <div style={{ fontSize: "1.3rem", fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>{callStats.connected || 0}</div>
                  <Text type="secondary" style={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b" }}>
                    Connected
                  </Text>
                </Flex>
                <CheckCircleOutlined style={{ fontSize: "1rem", color: "#000000ff" }} />
              </Flex>
            </Card>
          </Col>

          {/* Talk Time */}
          <Col xs={24} sm={12} lg={4} xl={4}>
            <Card bordered={false} className="user-kpi-card">
              <Flex justify="space-between" align="center">
                <Flex vertical gap={2}>
                  <div style={{ fontSize: "1.2rem", fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>{formatDuration(callStats.totalDuration)}</div>
                  <Text type="secondary" style={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b" }}>
                    Talk Time
                  </Text>
                </Flex>
                <ClockCircleOutlined style={{ fontSize: "1rem", color: "#000000ff" }} />
              </Flex>
            </Card>
          </Col>

          {/* Assigned Leads */}
          <Col xs={24} sm={12} lg={4} xl={4}>
            <Card bordered={false} className="user-kpi-card">
              <Flex justify="space-between" align="center">
                <Flex vertical gap={2}>
                  <div style={{ fontSize: "1.3rem", fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>{leads.length}</div>
                  <Text type="secondary" style={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b" }}>
                    My Leads
                  </Text>
                </Flex>
                <ContactsOutlined style={{ fontSize: "1rem", color: "#000000ff" }} />
              </Flex>
            </Card>
          </Col>

          {/* Today's Tasks */}
          <Col xs={24} sm={12} lg={4} xl={4}>
            <Card bordered={false} className="user-kpi-card">
              <Flex justify="space-between" align="center">
                <Flex vertical gap={2}>
                  <div style={{ fontSize: "1.3rem", fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>{upcomingFollowups.length}</div>
                  <Text type="secondary" style={{ fontSize: "0.75rem", fontWeight: 500, color: "#64748b" }}>
                    Today's Tasks
                  </Text>
                </Flex>
                <CalendarOutlined style={{ fontSize: "1rem", color: "#000000ff" }} />
              </Flex>
            </Card>
          </Col>

          {/* Revenue Target */}
          <Col xs={24} sm={12} lg={4} xl={4}>
            {(() => {
              let targetTagColor = "default";
              if (targetPercent >= 100) {
                targetTagColor = "success";
              } else if (targetPercent > 0) {
                targetTagColor = "processing";
              }
              return (
                <Card bordered={false} className="user-kpi-card user-revenue-card">
                  <Flex vertical gap={2}>
                    <Flex justify="space-between" align="center">
                      <Text type="secondary" style={{ fontSize: "0.72rem", fontWeight: 600, textTransform: "uppercase", color: "#64748b" }}>
                        Target
                      </Text>
                      <Tag color={targetTagColor} style={{ margin: 0, fontSize: "0.68rem", padding: "0 4px", lineHeight: "16px" }}>
                        {targetPercent}%
                      </Tag>
                    </Flex>
                    <div style={{ fontSize: "0.95rem", fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>
                      ₹{targetData.achievedAmount.toLocaleString()} {targetData.targetAmount > 0 ? <span style={{ fontSize: "0.72rem", color: "#94a3b8", fontWeight: 400 }}>/ ₹{targetData.targetAmount.toLocaleString()}</span> : <span style={{ fontSize: "0.68rem", color: "#16a34a", fontWeight: 500 }}>(Achieved)</span>}
                    </div>
                    <Progress percent={targetPercent} size="small" strokeColor="#10b981" showInfo={false} />
                  </Flex>
                </Card>
              );
            })()}
          </Col>
        </Row>

        {/* ── 4. MAIN CHARTS & RECORD SECTIONS ── */}
        <Row gutter={[16, 16]}>
          {/* Left Column: Call Trend Timeline & Recent Activity */}
          <Col xs={24} lg={15}>
            <Flex vertical gap={16}>
              {/* Call Trend Chart */}
              <Card
                bordered={false}
                className="user-panel-card"
                title={
                  <Flex vertical>
                    <Text type="secondary" style={{ fontSize: "0.72rem", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.05em", color: "#64748b" }}>
                      Performance
                    </Text>
                    <span style={{ fontSize: "0.95rem", fontWeight: 600, color: "#0f172a" }}>Call Trend</span>
                  </Flex>
                }
                extra={
                  <Radio.Group value={chartView} onChange={(e) => setChartView(e.target.value)} size="small" optionType="button" buttonStyle="solid">
                    <Radio.Button value="timeline">Timeline</Radio.Button>
                    <Radio.Button value="breakdown">Breakdown</Radio.Button>
                  </Radio.Group>
                }
              >
                <CallTrendTimelineChart chartView={chartView} period={period} totalCalls={totalCalls} connectedCalls={callStats.connected || 0} />
              </Card>

              {/* Stuck Deals (More Than 7 Days) Table */}
              <Card
                bordered={false}
                className="user-panel-card"
                title={
                  <Flex vertical>
                    <Text type="secondary" style={{ fontSize: "0.72rem", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.05em", color: "#64748b" }}>
                      Pipeline Alert
                    </Text>
                    <Flex align="center" gap={8}>
                      <span style={{ fontSize: "0.95rem", fontWeight: 600, color: "#0f172a" }}>
                        Stuck Deals <span style={{ fontSize: "0.76rem", fontWeight: 400, color: "#64748b" }}>(More Than 7 Days)</span>
                      </span>
                    </Flex>
                  </Flex>
                }
                extra={
                  <Button type="link" size="small" onClick={() => onNavigateTab?.("pipeline")} icon={<RightOutlined style={{ fontSize: 10 }} />} iconPosition="end" style={{ fontSize: "0.8rem", padding: 0 }}>
                    View Pipeline
                  </Button>
                }
              >
                <Table
                  dataSource={stuckDeals}
                  columns={stuckDealsColumns}
                  rowKey={(record, idx) => record._id || record.id || record.leadId || idx}
                  pagination={
                    stuckDeals.length > 5
                      ? {
                          pageSize: 5,
                          size: "small",
                          showSizeChanger: true,
                          pageSizeOptions: ["5", "10", "20"],
                          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} stuck deals`,
                        }
                      : false
                  }
                  scroll={{ x: totalStuckDealsTableWidth }}
                  components={{
                    header: {
                      cell: ResizableHeaderCell,
                    },
                  }}
                  size="small"
                  className="user-activity-table today-activities-table hide-scrollbar"
                  onRow={(record) => ({
                    onClick: () => onNavigateTab?.("pipeline", { search: record.leadCompanyName || record.dealName || "" }),
                    style: { cursor: "pointer" },
                  })}
                  locale={{
                    emptyText: (
                      <Flex vertical align="center" justify="center" style={{ padding: "1.5rem 0" }}>
                        <Text type="secondary" style={{ fontSize: "0.82rem" }}>
                          No stuck deals found (more than 7 days). All pipeline deals are active!
                        </Text>
                      </Flex>
                    ),
                  }}
                />
              </Card>
            </Flex>
          </Col>

          {/* Right Column: Call Mix Donut & Upcoming Follow-ups */}
          <Col xs={24} lg={9}>
            <Flex vertical gap={16}>
              {/* Call Mix Distribution */}
              <Card
                bordered={false}
                className="user-panel-card"
                title={
                  <Flex vertical>
                    <Text type="secondary" style={{ fontSize: "0.72rem", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.05em", color: "#64748b" }}>
                      Distribution
                    </Text>
                    <span style={{ fontSize: "0.95rem", fontWeight: 600, color: "#0f172a" }}>Call Mix</span>
                  </Flex>
                }
              >
                <Flex align="center" justify="space-between" gap={16} wrap="wrap" className="user-callmix-wrapper">
                  <div style={{ width: 120, height: 120, position: "relative", flexShrink: 0, margin: "0 auto" }}>
                    <CallMixPieChart stats={callStats} />
                  </div>

                  <div style={{ flex: 1, minWidth: 140 }} className="user-callmix-list">
                    <Flex justify="space-between" align="center" className="user-callmix-row">
                      <Flex align="center" gap={6}>
                        <Badge color={PALETTE.outgoing} />
                        <Text style={{ fontSize: "0.78rem", color: "#475569" }}>Outgoing Calls</Text>
                      </Flex>
                      <Text strong style={{ color: "#0f172a", fontSize: "0.85rem" }}>
                        {callStats.outgoing || 0}
                      </Text>
                    </Flex>

                    <Flex justify="space-between" align="center" className="user-callmix-row">
                      <Flex align="center" gap={6}>
                        <Badge color={PALETTE.incoming} />
                        <Text style={{ fontSize: "0.78rem", color: "#475569" }}>Incoming Calls</Text>
                      </Flex>
                      <Text strong style={{ color: "#0f172a", fontSize: "0.85rem" }}>
                        {callStats.incoming || 0}
                      </Text>
                    </Flex>

                    <Flex justify="space-between" align="center" className="user-callmix-row">
                      <Flex align="center" gap={6}>
                        <Badge color={PALETTE.missed} />
                        <Text style={{ fontSize: "0.78rem", color: "#475569" }}>Missed Calls</Text>
                      </Flex>
                      <Text strong style={{ color: "#0f172a", fontSize: "0.85rem" }}>
                        {callStats.missed || 0}
                      </Text>
                    </Flex>

                    <Flex justify="space-between" align="center" className="user-callmix-row">
                      <Flex align="center" gap={6}>
                        <Badge color={PALETTE.rejected} />
                        <Text style={{ fontSize: "0.78rem", color: "#475569" }}>Rejected Calls</Text>
                      </Flex>
                      <Text strong style={{ color: "#0f172a", fontSize: "0.85rem" }}>
                        {callStats.rejected || 0}
                      </Text>
                    </Flex>
                  </div>
                </Flex>
              </Card>

              {/* Today's Activity */}
              <Card
                bordered={false}
                className="user-panel-card"
                title={
                  <Flex vertical>
                    <Text type="secondary" style={{ fontSize: "0.72rem", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.05em", color: "#64748b" }}>
                      Today
                    </Text>
                    <span style={{ fontSize: "0.95rem", fontWeight: 600, color: "#0f172a" }}>Today's Activity</span>
                  </Flex>
                }
                extra={
                  <Button type="link" size="small" onClick={() => onNavigateTab?.("activities")} icon={<RightOutlined style={{ fontSize: 10 }} />} iconPosition="end" style={{ fontSize: "0.8rem", padding: 0 }}>
                    View All
                  </Button>
                }
              >
                {upcomingFollowups.length > 0 ? (
                  <Flex vertical gap={8}>
                    {upcomingFollowups.map((item) => (
                      <button type="button" key={item.id || item._id} className="user-followup-card" onClick={() => onNavigateTab?.("activities")}>
                        <Flex justify="space-between" align="center">
                          <Flex vertical gap={2}>
                            <Text strong style={{ color: "#0f172a", fontSize: "0.82rem" }}>
                              {item.title || item.leadCompanyName || item.companyName || "Follow-up Lead"}
                            </Text>
                            <Text type="secondary" style={{ fontSize: "0.72rem" }}>
                              {item.subtitle || item.contactName || item.contactNumber || "Primary Contact"}
                            </Text>
                          </Flex>
                          <Tag color={item.tagColor || "blue"} style={{ margin: 0, borderRadius: 6, fontWeight: 600, fontSize: "0.7rem" }}>
                            {item.tag || "Today"}
                          </Tag>
                        </Flex>
                      </button>
                    ))}
                  </Flex>
                ) : (
                  <Empty description="No activities scheduled for today." />
                )}
              </Card>
            </Flex>
            {/* ── 5. BRAND WATERMARK FOOTER ── */}
            <Flex vertical align="center" justify="center" style={{ margin: "2.5rem 0.5rem 1.5rem", userSelect: "none", pointerEvents: "none" }}>
              <div
                style={{
                  fontSize: "1.9rem",
                  fontWeight: 900,
                  color: "#e2e8f0",
                  letterSpacing: "-0.03em",
                  textAlign: "right",
                  lineHeight: 1.2,
                }}
              >
                dealvoicecrm.com
              </div>
            </Flex>
          </Col>
        </Row>
      </Flex>
    </Spin>
  );
}

export default UserOverview;
